import type { ReactNode } from 'react';
import { ClickableTerm } from './journeyContent';

// Contenido de los popups "cómo se hace X" — texto + capturas reales de los componentes de la app
// (no accesibles vía un video real, generadas renderizando los componentes reales con data mock
// — ver DevGlossaryHarness.tsx, que se descarta después de generar las imágenes). Igual que
// journeyContent.tsx: hardcodeado, sin panel admin. `text` acepta ReactNode para poder linkear a
// otro término del glosario (ClickableTerm) desde dentro de un step.

export interface GlossaryStep {
  text: ReactNode;
  image?: string;
}

export interface GlossaryTable {
  headers: string[];
  rows: string[][];
}

export interface GlossaryTerm {
  title: string;
  steps: GlossaryStep[];
  /** Tabla opcional para datos tabulares (ej: rangos de días → plazo de aviso). Se renderiza
   *  debajo de los steps, antes de note. */
  table?: GlossaryTable;
  note?: string;
}

export const JOURNEY_GLOSSARY: Record<string, GlossaryTerm> = {
  'agendar-proxima-sesion': {
    title: 'Cómo agendar la próxima sesión',
    steps: [
      { text: 'Andá a la tab "Pacientes".' },
      {
        text: 'Ubicá al paciente en la lista — si no tiene una próxima sesión vas a ver un botón "Agendar" en esa fila (también disponible en el menú de tres puntos).',
        image: '/journey-glossary/agendar-paso1-lista-pacientes.png',
      },
      { text: 'Al hacer click te lleva a la Agenda con el paciente ya preseleccionado.' },
      {
        text: 'Elegí un horario libre en el calendario (arrastrando o haciendo click) — se abre el modal "Agendar una sesión".',
        image: '/journey-glossary/agendar-paso2-seleccionar.png',
      },
      { text: 'Confirmá el paciente en el campo "Paciente" y el tipo de cita, y tocá "Confirmar".' },
      {
        text: 'Revisá los datos en la confirmación final — el sistema exige al menos 6hs de anticipación.',
        image: '/journey-glossary/agendar-paso3-confirmar.png',
      },
    ],
    note: 'No hay un botón de agendar dentro de la ficha del paciente — el punto de partida siempre es la lista de Pacientes.',
  },
  'formulario-no-agendamiento': {
    title: 'Cómo completar el formulario de no agendamiento',
    steps: [
      { text: 'Andá a la tab "Pacientes".' },
      { text: 'Ubicá al paciente en la lista y abrí el menú de esa fila (los tres puntos).' },
      {
        text: 'Hacé click en "Formulario de no agendamiento".',
        image: '/journey-glossary/formulario-no-agendamiento.png',
      },
      {
        text: 'Se abre un formulario con los datos del paciente ya precargados — completá el motivo por el que no agendaste la próxima sesión (alta consensuada, el paciente decidió no continuar, no pudieron combinar horarios, o se va de vacaciones/viaje/mudanza).',
      },
      { text: 'Ese motivo es lo que define el seguimiento automático que el sistema le manda al paciente.' },
    ],
  },
  'bloqueo-sin-horarios': {
    title: '⏳ Bloqueo automático — sin horarios de reprogramación',
    steps: [
      { text: 'El sistema necesita que tengas siempre al menos 4 horarios de reprogramación libres (sin ocuparse) en los próximos 7 días — son los horarios que un paciente puede usar si necesita mover una sesión ya agendada.' },
      { text: 'En el momento en que dejás de tener esos 4 horarios libres, el sistema lo detecta y te bloquea automáticamente para pacientes nuevos. Los procesos que ya tenés no se ven afectados.' },
      { text: 'Se desbloquea automáticamente en cuanto volvés a tener 4 horarios de reprogramación libres.' },
    ],
    note: 'Es 100% automático — no hace falta escribirle a nadie, ni para bloquearlo ni para desbloquearlo.',
  },
  'bloqueo-demasiados-pacientes': {
    title: '📈 Bloqueo automático — demasiados pacientes',
    steps: [
      { text: 'Cada psicóloga tiene un límite de procesos vigentes al mismo tiempo — por defecto 40.' },
      { text: 'Al alcanzar ese límite, el sistema te bloquea automáticamente para pacientes nuevos hasta que baje la cantidad de procesos activos.' },
      { text: 'Si preferís un límite más bajo, podés pedirlo escribiendo a administracion@psimammoliti.com.' },
    ],
  },
  'bloqueo-manual': {
    title: '✍️ Situaciones personales extraordinarias',
    steps: [
      { text: 'Cualquier situación que implique que tengas que dejar de atender, sin fecha de reincorporación programada.' },
      {
        text: (
          <>
            Si ya conocés la fecha de fin de esta situación, lo que corresponde solicitar es{' '}
            <ClickableTerm term="bloqueo-licencias">Días Off</ClickableTerm>, no esto.
          </>
        ),
      },
      { text: 'Si te encontrás en esta situación, escribí a administracion@psimammoliti.com — el equipo lo activa manualmente y te avisa cuándo y cómo se vuelve a desbloquear.' },
    ],
    note: '🚨 Esto aplica solo para nuevos pacientes — tus pacientes ya asignados continúan atendiéndose con normalidad.',
  },
  'bloqueo-licencias': {
    title: '🏖️ Bloqueo por Días Off',
    steps: [
      { text: 'Cualquier situación que implique que tengas que dejar de atender por unos días específicos. Sabés exactamente la fecha de reincorporación.' },
    ],
    table: {
      headers: ['Días tomados', 'Aviso previo'],
      rows: [
        ['Hasta 9 días', 'No necesitás avisar con anticipación ni se aplica un bloqueo previo.'],
        ['10 a 15 días', 'Avisá con al menos 30 días de anticipación.'],
        ['16 a 30 días', 'Avisá con al menos 60 días de anticipación.'],
        ['Más de 30 días', 'Avisá con al menos 90 días de anticipación. Al volver, tenés además un bloqueo adicional por el doble del tiempo que estuviste de Días Off.'],
      ],
    },
    note: 'Días Off incluye licencias y vacaciones, además de las situaciones personales que ya tienen fecha de inicio y fin definidas. Cuanto más largo el período, con más anticipación hay que avisarlo — así el equipo puede reorganizar tu cartera de pacientes a tiempo.\n\n🚨 Esto aplica solo para nuevos pacientes — tus pacientes ya asignados continúan atendiéndose con normalidad.',
  },
};
